import axios from "axios";
import { useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Alert, Button, Col, Container, Form, Row } from "react-bootstrap";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { HiArrowRight } from "react-icons/hi";
import { MdEmail, MdLocationOn, MdLock } from "react-icons/md";
import "../../../../assets/css/customer/auth/SignUpPage.css";
import AppContext from "../../../../contexts/AppContext";
import TermsModal from "../../../../components/customer/termsconditions/TermsConditions.js";

const SignUpPage = () => {
  const navigate = useNavigate();
  const {
    baseUrl,
    regExpChecker,
    setCustomer,
    setIsAuthenticated,
    setCustomerToken,
  } = useContext(AppContext);
  const [email, setEmail] = useState("");
  const [city, setCity] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [signupLoading, setSignupLoading] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errors, setErrors] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    terms: "",
  });
  const [showPasswordError, setShowPasswordError] = useState(false);

  const togglePassword = () => setShowPassword(!showPassword);
  const toggleConfirmPassword = () =>
    setShowConfirmPassword(!showConfirmPassword);

  const hasBlockingErrors = () =>
    Boolean(
      errors.email ||
        errors.password ||
        errors.confirmPassword ||
        !email.trim() ||
        !password.trim() ||
        !confirmPassword.trim() ||
        password !== confirmPassword,
    );

  const Signupcustomer = () => {
    setErrorMessage("");

    if (!agreeTerms) {
      setErrors((prev) => ({
        ...prev,
        terms: "Please agree to the Terms & Conditions to continue.",
      }));
      return;
    }

    if (password !== confirmPassword) {
      setShowPasswordError(true);
      return;
    }

    if (hasBlockingErrors()) {
      setErrorMessage("Please fix the highlighted errors before continuing.");
      return;
    }

    setSignupLoading(true);

    const formData = new FormData();
    formData.append("email", email);
    formData.append("city", city);
    formData.append("password", password);
    formData.append("confirmPassword", confirmPassword);
    formData.append("agreeTerms", agreeTerms);

    axios
      .post(`${baseUrl}/customer/signup`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        setSignupLoading(false);

        if (response.data.success) {
          // setCustomerToken persists the token to localStorage under
          // "customerAuthToken" already — no separate "authToken" needed.
          localStorage.setItem(
            "loggedInCustomer",
            JSON.stringify(response.data.customer),
          );
          setCustomerToken(response.data.token);
          setCustomer(response.data.customer);
          setIsAuthenticated(true);
          navigate("/");
        } else {
          setErrorMessage(response.data.message || "Signup failed. Try again.");
        }
      })
      .catch((error) => {
        console.log(error);
        setSignupLoading(false);
        setErrorMessage(
          error.response?.data?.message || "Something went wrong. Try again.",
        );
      });
  };

  return (
    <div className="signup-glassmorphism-signup-container">
      <div className="signup-background-circles">
        <div className="signup-circle signup-circle-1"></div>
        <div className="signup-circle signup-circle-2"></div>
        <div className="signup-circle signup-circle-3"></div>
        <div className="signup-circle signup-circle-4"></div>
      </div>

      <Container fluid className="signup-content-wrapper">
        <Row className="signup-row w-100 g-0 align-items-center">
          <Col xl={6} lg={6} md={12} xs={12} className="signup-brand-section">
            <div className="signup-logo-container">
              <h1 className="signup-logo-text">CRAFTHIVE</h1>
            </div>
            <h2 className="signup-main-heading">
              Discover Handmade, Made With Love
            </h2>
            <p className="signup-description-text">
              Explore one-of-a-kind handmade treasures crafted with care. From
              unique gifts to everyday essentials, find pieces that tell a
              story. Join CraftHive and start shopping today.
            </p>
          </Col>
          <Col xl={6} lg={6} md={12} xs={12} className="signup-form-section">
            <div className="signup-glass-card">
              <h3 className="signup-form-title">CREATE YOUR ACCOUNT</h3>
              <p className="signup-form-subtitle">
                Sign up to start shopping handmade
              </p>

              {errorMessage && (
                <Alert variant="danger" className="mb-3">
                  {errorMessage}
                </Alert>
              )}

              <Form className="signup-form">
                <Form.Group className="signup-input-group-custom">
                  <div className="signup-input-icon">
                    <MdEmail size={18} />
                  </div>
                  <Form.Control
                    type="email"
                    placeholder="Email Address"
                    className="signup-glass-input"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setErrors((prev) => ({
                        ...prev,
                        email: regExpChecker("email", e.target.value),
                      }));
                      setErrorMessage("");
                    }}
                  />
                  {errors.email && (
                    <div className="text-danger mt-1 signup-error-text">
                      {errors.email}
                    </div>
                  )}
                </Form.Group>

                <Form.Group className="signup-input-group-custom">
                  <div className="signup-input-icon">
                    <MdLocationOn size={18} />
                  </div>
                  <Form.Control
                    type="text"
                    name="city"
                    placeholder="City"
                    className="signup-glass-input"
                    value={city}
                    onChange={(e) => {
                      setCity(e.target.value);
                    }}
                  />
                </Form.Group>

                <Form.Group className="signup-input-group-custom">
                  <div className="signup-input-icon">
                    <MdLock size={18} />
                  </div>
                  <Form.Control
                    type={showPassword ? "text" : "password"}
                    placeholder="Password"
                    className="signup-glass-input"
                    value={password}
                    onChange={(e) => {
                      const newPassword = e.target.value;
                      setPassword(newPassword);
                      setErrors((prev) => ({
                        ...prev,
                        password: regExpChecker("password", newPassword),
                      }));
                      if (confirmPassword) {
                        setShowPasswordError(newPassword !== confirmPassword);
                      }
                      setErrorMessage("");
                    }}
                  />
                  {errors.password && (
                    <div className="text-danger mt-1 signup-error-text">
                      {errors.password}
                    </div>
                  )}
                  <div
                    className="signup-password-toggle"
                    onClick={togglePassword}
                  >
                    {showPassword ? (
                      <AiOutlineEyeInvisible size={20} />
                    ) : (
                      <AiOutlineEye size={20} />
                    )}
                  </div>
                </Form.Group>

                <Form.Group className="signup-input-group-custom">
                  <div className="signup-input-icon">
                    <MdLock size={18} />
                  </div>
                  <Form.Control
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm Password"
                    className="signup-glass-input"
                    value={confirmPassword}
                    onChange={(e) => {
                      const newConfirmPassword = e.target.value;
                      setConfirmPassword(newConfirmPassword);
                      setErrors((prev) => ({
                        ...prev,
                        confirmPassword: regExpChecker("confirmPassword", {
                          password,
                          confirmPassword: newConfirmPassword,
                        }),
                      }));
                      setShowPasswordError(
                        newConfirmPassword.length > 0 &&
                          password !== newConfirmPassword,
                      );
                      setErrorMessage("");
                    }}
                  />
                  {errors.confirmPassword && (
                    <div className="text-danger mt-1 signup-error-text">
                      {errors.confirmPassword}
                    </div>
                  )}
                  <div
                    className="signup-password-toggle"
                    onClick={toggleConfirmPassword}
                  >
                    {showConfirmPassword ? (
                      <AiOutlineEyeInvisible size={20} />
                    ) : (
                      <AiOutlineEye size={20} />
                    )}
                  </div>
                  {showPasswordError && (
                    <div className="text-danger mt-1 signup-error-text">
                      Passwords do not match.
                    </div>
                  )}
                </Form.Group>

                <Form.Group className="signup-terms-group">
                  <Form.Check
                    type="checkbox"
                    id="agreeTerms"
                    className="signup-terms-label"
                  >
                    <Form.Check.Input
                      type="checkbox"
                      name="agreeTerms"
                      className="signup-terms-checkbox"
                      checked={agreeTerms}
                      onChange={(e) => {
                        setAgreeTerms(e.target.checked);
                        setErrors((prev) => ({ ...prev, terms: "" }));
                      }}
                    />
                    <Form.Check.Label className="signup-terms-text">
                      I agree to the{" "}
                      <a
                        href="#terms"
                        className="signup-terms-link"
                        onClick={(e) => {
                          e.preventDefault();
                          setShowTermsModal(true);
                        }}
                      >
                        Terms & Conditions
                      </a>
                    </Form.Check.Label>
                  </Form.Check>
                  {errors.terms && (
                    <div className="text-danger mt-1 signup-error-text">
                      {errors.terms}
                    </div>
                  )}
                </Form.Group>

                <Button
                  type="button"
                  className="signup-button"
                  disabled={signupLoading}
                  onClick={Signupcustomer}
                >
                  {signupLoading ? "SIGNING UP..." : "SIGN UP"}
                  <HiArrowRight size={18} />
                </Button>
              </Form>

              <p className="signup-login-link-text">
                Already have an account?{" "}
                <Link to="/login" className="signup-login-link">
                  Login
                </Link>
              </p>
            </div>
          </Col>
        </Row>
      </Container>

      <TermsModal
        show={showTermsModal}
        onClose={() => setShowTermsModal(false)}
      />
    </div>
  );
};

export default SignUpPage;